<template>
  <main class="security-center">
    <PageHeader icon="fas fa-user-shield">
      <template #title>Manajemen Pengguna & Akses Cabang</template>
      <template #subtitle
        >Undang pengguna, atur role, batasi cabang, dan kelola keamanan akun dari
        satu tempat</template
      >
    </PageHeader>

    <div class="security-kpis">
      <article>
        <i class="fas fa-users"></i>
        <div>
          <span>Total pengguna</span><strong>{{ totalRows }}</strong>
        </div>
      </article>
      <article>
        <i class="fas fa-user-lock"></i>
        <div>
          <span>Perlu perhatian</span><strong>{{ attentionCount }}</strong>
        </div>
      </article>
      <article>
        <i class="fas fa-mobile-alt"></i>
        <div>
          <span>MFA aktif</span><strong>{{ mfaCount }}</strong>
        </div>
      </article>
      <article>
        <i class="fas fa-shield-alt"></i>
        <div>
          <span>Role tersedia</span><strong>{{ roles.length }}</strong>
        </div>
      </article>
    </div>

    <b-tabs v-model="activeTab" class="security-tabs" content-class="pt-4">
      <b-tab title="Pengguna & Sesi">
        <section class="security-card">
          <div class="security-toolbar">
            <b-input-group
              ><template #prepend
                ><b-input-group-text
                  ><i class="fas fa-search"></i></b-input-group-text></template
              ><b-form-input
                v-model="filters.search"
                placeholder="Cari nama atau email"
                @input="scheduleUsers"
            /></b-input-group>
            <b-form-select
              v-model="filters.status"
              :options="statusOptions"
              @change="loadUsers(1)"
            />
            <div class="toolbar-actions">
              <b-button variant="outline-primary" @click="loadUsers(page)"
                ><i class="fas fa-sync-alt mr-2"></i>Segarkan</b-button
              ><b-button
                variant="primary"
                @click="openInvite"
                ><i class="fas fa-user-plus mr-2"></i>Undang</b-button
              >
            </div>
          </div>
          <b-table
            :items="users"
            :fields="userFields"
            responsive
            hover
            show-empty
            empty-text="Tidak ada pengguna"
            :busy="loadingUsers"
          >
            <template #cell(name)="d"
              ><div class="identity-cell">
                <span>{{ initials(d.item.name) }}</span>
                <div>
                  <strong>{{ d.item.name }}</strong
                  ><small>{{ d.item.email }}</small>
                </div>
              </div></template
            >
            <template #cell(status)="d"
              ><span class="status-pill" :class="'is-' + d.value">{{
                statusLabel(d.value)
              }}</span></template
            >
            <template #cell(branches)="d">
              <div class="branch-chip-list">
                <span v-for="branch in d.value" :key="branch">{{ branch }}</span>
                <em v-if="!d.value || !d.value.length">Belum diatur</em>
              </div>
            </template>
            <template #cell(security)="d"
              ><div class="security-flags">
                <span :class="d.item.mfa_enabled ? 'ok' : 'warn'"
                  ><i class="fas fa-mobile-alt"></i>
                  {{ d.item.mfa_enabled ? "MFA" : "Tanpa MFA" }}</span
                ><span v-if="d.item.risk_count" class="danger"
                  ><i class="fas fa-exclamation-triangle"></i>
                  {{ d.item.risk_count }} konflik</span
                >
              </div></template
            >
            <template #cell(last_login_at)="d"
              >{{ formatDate(d.value)
              }}<small class="d-block text-muted">{{
                d.item.last_login_ip || "-"
              }}</small></template
            >
            <template #cell(active_sessions)="d"
              ><strong>{{ d.value }}</strong> sesi</template
            >
            <template #cell(actions)="d"
              ><b-button size="sm" variant="primary" @click="openUser(d.item)"
                >Kelola</b-button
              ></template
            >
          </b-table>
          <footer class="security-footer">
            <span>{{ users.length }} dari {{ totalRows }} pengguna</span
            ><b-pagination
              v-model="page"
              :total-rows="totalRows"
              :per-page="pageSize"
              @change="loadUsers"
            />
          </footer>
        </section>
      </b-tab>

      <b-tab title="Undangan & Aktivasi" @click="loadInvitations">
        <section class="security-card">
          <div class="section-heading">
            <div>
              <h3>Aktivasi pengguna baru</h3>
              <p>Pantau undangan yang masih menunggu, sudah diterima, atau kedaluwarsa.</p>
            </div>
            <b-button variant="primary" @click="openInvite">
              <i class="fas fa-user-plus mr-2"></i>Buat undangan
            </b-button>
          </div>
          <b-table
            :items="invitations"
            :fields="invitationFields"
            responsive
            hover
            show-empty
            empty-text="Belum ada undangan pengguna"
          >
            <template #cell(status)="d">
              <span class="status-pill" :class="'is-' + d.value">{{ invitationStatusLabel(d.value) }}</span>
            </template>
            <template #cell(created_at)="d">{{ formatDate(d.value) }}</template>
            <template #cell(expires_at)="d">{{ formatDate(d.value) }}</template>
            <template #cell(accepted_at)="d">{{ formatDate(d.value) }}</template>
            <template #cell(actions)="d">
              <div class="table-actions" v-if="['pending','expired'].includes(d.item.status)">
                <b-button size="sm" variant="outline-primary" @click="resendInvitation(d.item)">Kirim ulang</b-button>
                <b-button v-if="d.item.status === 'pending'" size="sm" variant="outline-danger" @click="revokeInvitation(d.item)">Batalkan</b-button>
              </div>
            </template>
          </b-table>
        </section>
      </b-tab>

      <b-tab title="Persetujuan Akses" @click="loadApprovals">
        <section class="security-card">
          <div class="section-heading"><div><h3>Maker–checker akses sensitif</h3><p>Pemberi akses dan penyetuju wajib orang berbeda. Perubahan diterapkan otomatis setelah disetujui.</p></div><b-button variant="outline-primary" @click="loadApprovals"><i class="fas fa-sync-alt mr-2"></i>Segarkan</b-button></div>
          <b-table :items="approvals" :fields="approvalFields" responsive hover show-empty empty-text="Tidak ada permintaan persetujuan">
            <template #cell(status)="d"><span class="status-pill" :class="'is-' + d.value">{{ approvalStatusLabel(d.value) }}</span></template>
            <template #cell(created_at)="d">{{ formatDate(d.value) }}</template>
            <template #cell(actions)="d"><div class="table-actions" v-if="d.item.status === 'pending'"><b-button size="sm" variant="success" @click="decideApproval(d.item,'approve')">Setujui</b-button><b-button size="sm" variant="outline-danger" @click="decideApproval(d.item,'reject')">Tolak</b-button></div></template>
          </b-table>
        </section>
      </b-tab>

      <b-tab title="Role & Template" @click="loadRoles">
        <section class="security-card">
          <div class="access-model-guide">
            <div><span>1</span><strong>Akses bawaan jabatan</strong><small>Baseline otomatis sesuai pekerjaan utama.</small></div>
            <i class="fas fa-plus"></i>
            <div><span>2</span><strong>Role akses</strong><small>Paket tugas fungsional atau penugasan sementara.</small></div>
            <i class="fas fa-plus"></i>
            <div><span>3</span><strong>Hak akses khusus</strong><small>Pengecualian terakhir per orang dan wajib beralasan.</small></div>
          </div>
          <div class="section-heading">
            <div>
              <h3>Template role fungsional</h3>
              <p>
                Role bukan jabatan HR. Gunakan untuk mengemas izin suatu tugas, lalu berikan kepada pengguna yang membutuhkannya.
              </p>
            </div>
            <div class="toolbar-actions">
              <b-button variant="outline-primary" @click="$router.push('/user-managements/position-access')"><i class="fas fa-briefcase mr-2"></i>Akses Bawaan Jabatan</b-button>
              <b-button variant="primary" @click="newRole"><i class="fas fa-plus mr-2"></i>Role baru</b-button>
            </div>
          </div>
          <div class="role-grid">
            <article v-for="role in roles" :key="role.id" class="role-card">
              <div>
                <span class="role-icon"><i class="fas fa-id-badge"></i></span
                ><span v-if="role.is_system" class="template-label"
                  >Template</span
                >
              </div>
              <h4>{{ role.name }}</h4>
              <p>{{ role.description }}</p>
              <footer>
                <span
                  >{{ (role.access_codes || []).length }} izin ·
                  {{ role.user_count }} pengguna</span
                ><b-button size="sm" variant="light" @click="editRole(role)"
                  >Atur</b-button
                >
              </footer>
            </article>
          </div>
        </section>
      </b-tab>

      <b-tab title="Review Akses" @click="loadReviews">
        <section class="security-card">
          <div class="section-heading">
            <div>
              <h3>Sertifikasi akses berkala</h3>
              <p>
                Pastikan setiap pengguna masih membutuhkan akses yang
                dimilikinya.
              </p>
            </div>
            <b-button variant="primary" @click="$bvModal.show('review-modal')"
              ><i class="fas fa-clipboard-check mr-2"></i>Mulai review</b-button
            >
          </div>
          <b-table
            :items="reviews"
            :fields="reviewFields"
            responsive
            show-empty
            empty-text="Belum ada review akses"
          >
            <template #cell(status)="d"
              ><span class="status-pill" :class="'is-' + d.value">{{
                d.value
              }}</span></template
            >
            <template #cell(progress)="d"
              >{{ d.item.total - d.item.pending }} /
              {{ d.item.total }}</template
            >
            <template #cell(due_date)="d">{{
              formatDate(d.value, false)
            }}</template>
            <template #cell(actions)="d"
              ><b-button
                size="sm"
                variant="outline-primary"
                @click="openReview(d.item)"
                >Buka</b-button
              ></template
            >
          </b-table>
        </section>
      </b-tab>

      <b-tab title="Kebijakan Keamanan" @click="loadPolicy">
        <section class="security-card policy-card" v-if="policy">
          <div class="section-heading">
            <div>
              <h3>Baseline keamanan tenant</h3>
              <p>Berlaku untuk seluruh akun klinik.</p>
            </div>
            <b-button variant="primary" @click="savePolicy"
              >Simpan kebijakan</b-button
            >
          </div>
          <div class="policy-grid">
            <b-form-group label="Panjang password minimum"
              ><b-form-input
                v-model.number="policy.password_min_length"
                type="number"
                min="8"
                max="64"
            /></b-form-group>
            <b-form-group label="Percobaan login maksimum"
              ><b-form-input
                v-model.number="policy.max_failed_attempts"
                type="number"
                min="3"
                max="20"
            /></b-form-group>
            <b-form-group label="Durasi lockout (menit)"
              ><b-form-input
                v-model.number="policy.lockout_minutes"
                type="number"
                min="1"
            /></b-form-group>
            <b-form-group label="Timeout sesi (menit)"
              ><b-form-input
                v-model.number="policy.session_timeout_minutes"
                type="number"
                min="15"
            /></b-form-group>
            <b-form-group label="Kedaluwarsa password (hari)"
              ><b-form-input
                v-model.number="policy.password_expiry_days"
                type="number"
                min="0"
            /></b-form-group>
            <b-form-group label="Akun dormant (hari)"
              ><b-form-input
                v-model.number="policy.inactive_account_days"
                type="number"
                min="0"
            /></b-form-group>
          </div>
          <div class="policy-switches">
            <b-form-checkbox v-model="policy.password_require_uppercase" switch
              >Wajib huruf besar</b-form-checkbox
            ><b-form-checkbox v-model="policy.password_require_number" switch
              >Wajib angka</b-form-checkbox
            ><b-form-checkbox v-model="policy.password_require_symbol" switch
              >Wajib simbol</b-form-checkbox
            ><b-form-checkbox v-model="policy.mfa_required_for_admin" switch
              >Wajib MFA untuk admin</b-form-checkbox
            >
          </div>
        </section>
      </b-tab>
    </b-tabs>

    <b-modal
      id="user-security-modal"
      size="xl"
      title="Kelola Keamanan Pengguna"
      hide-footer
    >
      <div v-if="selectedUser" class="user-detail">
        <header>
          <div class="identity-cell">
            <span>{{ initials(selectedUser.name) }}</span>
            <div>
              <h4>{{ selectedUser.name }}</h4>
              <small
                >{{ selectedUser.email }} ·
                {{ selectedUser.position_name }}</small
              >
            </div>
          </div>
          <span class="status-pill" :class="'is-' + selectedUser.status">{{
            statusLabel(selectedUser.status)
          }}</span>
        </header>
        <b-alert :show="userConflicts.length > 0" variant="warning"
          ><strong
            >{{ userConflicts.length }} konflik pemisahan tugas
            terdeteksi.</strong
          >
          Review role atau izin langsung pengguna ini.</b-alert
        >
        <div class="detail-grid">
          <section>
            <h5>Status & masa akses</h5>
            <b-form-select
              v-model="userForm.status"
              :options="statusOptions.slice(1)"
            /><b-form-input
              v-model="userForm.access_expires_at"
              type="datetime-local"
              class="mt-2"
            /><b-form-input
              v-model="userForm.reason"
              placeholder="Alasan perubahan (wajib)"
              class="mt-2"
            /><b-button
              block
              variant="primary"
              class="mt-2"
              @click="saveUserStatus"
              >Simpan status</b-button
            >
          </section>
          <section>
            <h5>Tindakan keamanan</h5>
            <b-button block variant="outline-danger" @click="forceLogout"
              >Cabut semua sesi</b-button
            ><b-button
              block
              variant="outline-warning"
              class="mt-2"
              @click="resetPassword"
              >Buat password sementara</b-button
            ><b-button
              v-if="selectedUser.mfa_enabled"
              block
              variant="outline-secondary"
              class="mt-2"
              @click="disableMfa"
              >Reset MFA</b-button
            >
          </section>
        </div>
        <section class="role-assignment mt-3">
          <h5>Role fungsional tambahan</h5>
          <p class="text-muted">Akses bawaan jabatan tetap berlaku. Tambahkan role hanya untuk tugas lain atau penugasan sementara.</p>
          <div class="role-assign-form">
            <b-form-group label="Role"><b-form-select v-model="assignForm.role_id" :options="roleOptions" /></b-form-group>
            <b-form-group label="Berlaku di cabang"><b-form-select v-model="assignForm.branch_id" :options="roleBranchOptions" /></b-form-group>
            <b-form-group label="Mulai berlaku"><b-form-input v-model="assignForm.valid_from" type="date" /></b-form-group>
            <b-form-group label="Berakhir"><b-form-input v-model="assignForm.valid_until" type="date" /></b-form-group>
            <b-form-group label="Alasan"><b-form-input v-model="assignForm.reason" placeholder="Contoh: penanggung jawab kasir Dago" /></b-form-group>
            <b-button class="role-assign-submit" variant="primary" @click="assignRole"
              >Berikan role</b-button
            >
          </div>
          <div class="assigned-role-list">
            <span v-for="role in assignedRoles" :key="role.id"
              ><strong>{{ role.name }}</strong
              ><small>{{ role.branch_name || 'Seluruh cabang' }}<template v-if="role.valid_until"> · sampai {{ formatDate(role.valid_until, false) }}</template></small
              ><button
                type="button"
                title="Cabut role"
                @click="revokeRole(role)"
              >
                &times;
              </button></span
            ><em v-if="!assignedRoles.length">Belum ada role khusus.</em>
          </div>
        </section>
        <section class="branch-assignment mt-3">
          <div class="section-heading compact">
            <div>
              <h5>Akses cabang</h5>
              <p>Pilih lokasi kerja, cabang default setelah login, dan masa berlaku akses.</p>
            </div>
            <b-button variant="primary" @click="saveUserBranchAccess">Simpan cabang</b-button>
          </div>
          <b-alert v-if="selectedUser.user_type_id === 1" show variant="info">
            Ini mengatur akses akun dokter. Jadwal praktik, poli, dan SIP per cabang tetap dikelola pada Jaringan & Multi-Cabang.
          </b-alert>
          <div class="branch-access-table">
            <div v-for="row in branchForm.rows" :key="row.branch_id" class="branch-access-row" :class="{ selected: row.assigned }">
              <b-form-checkbox v-model="row.assigned" @change="normalizeBranchDefault"><strong>{{ branchName(row.branch_id) }}</strong></b-form-checkbox>
              <b-form-input v-model="row.valid_from" type="date" :disabled="!row.assigned" aria-label="Mulai akses cabang" />
              <b-form-input v-model="row.valid_until" type="date" :disabled="!row.assigned" aria-label="Akhir akses cabang" />
            </div>
            <div class="branch-access-settings">
              <b-form-group label="Cabang default">
                <b-form-select v-model="branchForm.default_branch_id" :options="selectedBranchOptions" />
              </b-form-group>
              <div class="consolidated-option" :class="{ disabled: selectedBranchRows.length < 2 }">
                <b-form-checkbox v-model="branchForm.can_view_consolidated" switch :disabled="selectedBranchRows.length < 2">Dashboard konsolidasi</b-form-checkbox>
                <small>Menggabungkan ringkasan cabang terpilih; tidak memberi akses ke cabang lain.</small>
              </div>
              <b-form-group label="Alasan perubahan" class="mt-3"><b-form-input v-model="branchForm.reason" placeholder="Contoh: Rotasi kerja semester II" /></b-form-group>
            </div>
          </div>
        </section>
        <div class="effective-access-heading mt-4">
          <div><h5>Akses efektif <span class="text-muted">({{ effectiveCodes.length }})</span></h5><p>Hasil gabungan otomatis; kode yang sama tidak dihitung dua kali.</p></div>
          <b-button size="sm" variant="outline-primary" @click="$router.push('/user-managements/user-access'); $bvModal.hide('user-security-modal')">Kelola pengecualian</b-button>
        </div>
        <b-alert :show="redundantAccess.length > 0" variant="warning">{{ redundantAccess.length }} izin lama masih berasal dari lebih dari satu sumber. Hasil akses tetap konsisten, tetapi sebaiknya dirapikan saat review berikutnya.</b-alert>
        <div class="access-source-grid">
          <section><h6><i class="fas fa-briefcase"></i> Dari jabatan</h6><div class="access-chip-list"><span v-for="code in (accessSources.position || [])" :key="'p'+code">{{ code }}</span><em v-if="!(accessSources.position || []).length">Tidak ada</em></div></section>
          <section><h6><i class="fas fa-layer-group"></i> Dari role</h6><div class="access-chip-list"><span v-for="code in (accessSources.role || [])" :key="'r'+code">{{ code }}</span><em v-if="!(accessSources.role || []).length">Tidak ada</em></div></section>
          <section><h6><i class="fas fa-user-cog"></i> Hak khusus</h6><div class="access-chip-list"><span v-for="code in (accessSources.direct || [])" :key="'d'+code">{{ code }}</span><em v-if="!(accessSources.direct || []).length">Tidak ada</em></div></section>
        </div>
        <h5 class="mt-4">Sesi aktif</h5>
        <b-table
          :items="sessions"
          :fields="sessionFields"
          small
          responsive
          show-empty
          empty-text="Tidak ada sesi aktif"
          ><template #cell(created_at)="d">{{ formatDate(d.value) }}</template
          ><template #cell(expires_at)="d">{{ formatDate(d.value) }}</template
          ><template #cell(actions)="d"
            ><b-button
              size="sm"
              variant="outline-danger"
              @click="revokeSession(d.item.id)"
              >Cabut</b-button
            ></template
          ></b-table
        >
      </div>
    </b-modal>

    <b-modal id="role-modal" size="lg" title="Konfigurasi Role Akses" @ok.prevent="saveRole" ok-title="Simpan role" cancel-title="Batal">
      <b-alert show variant="info">Role adalah paket tugas fungsional, bukan jabatan karyawan. Berikan izin secukupnya sesuai prinsip least privilege.</b-alert>
      <div class="role-form-grid">
        <b-form-group label="Nama role" description="Contoh: Supervisor Kasir atau Koordinator Farmasi">
          <b-form-input v-model="roleForm.name" @input="syncRoleCode" placeholder="Nama yang mudah dipahami" />
        </b-form-group>
        <b-form-group label="Kode internal" description="Dibuat otomatis dan tidak tampil ke pengguna operasional">
          <b-form-input v-model="roleForm.code" :disabled="!!roleForm.id" placeholder="supervisor_kasir" />
        </b-form-group>
      </div>
      <b-form-group label="Tujuan role" description="Jelaskan kapan role ini boleh diberikan">
        <b-form-textarea v-model="roleForm.description" rows="2" placeholder="Tanggung jawab dan batas penggunaan role" />
      </b-form-group>
      <b-form-group label="Alasan perubahan" description="Tercatat di audit trail dan dipakai pada alur persetujuan jika izin sensitif berubah">
        <b-form-input v-model="roleForm.reason" placeholder="Contoh: menyesuaikan tanggung jawab supervisor farmasi" />
      </b-form-group>
      <b-form-group label="Cari izin">
        <b-input-group><template #prepend><b-input-group-text><i class="fas fa-search"></i></b-input-group-text></template><b-form-input v-model="permissionSearch" placeholder="Cari fitur, nama izin, atau kode" /></b-input-group>
      </b-form-group>
      <div class="permission-picker">
        <b-form-checkbox-group v-model="roleForm.access_codes" :options="filteredPermissionOptions" stacked />
        <p v-if="!filteredPermissionOptions.length">Izin tidak ditemukan.</p>
      </div>
      <div class="role-form-footer">
        <span><strong>{{ (roleForm.access_codes || []).length }}</strong> izin dipilih</span>
        <b-form-checkbox v-if="roleForm.id" v-model="roleForm.is_active" switch>Role aktif</b-form-checkbox>
      </div>
    </b-modal>
    <b-modal
      id="review-modal"
      title="Mulai Review Akses"
      @ok.prevent="createReview"
      ><b-form-group label="Nama review"
        ><b-form-input v-model="reviewForm.name" /></b-form-group
      ><b-form-group label="Reviewer"
        ><b-form-select
          v-model="reviewForm.reviewer_id"
          :options="userOptions" /></b-form-group
      ><b-form-group label="Batas waktu"
        ><b-form-input v-model="reviewForm.due_date" type="date"
      /></b-form-group><b-form-group label="Cakupan review"><b-form-select v-model="reviewForm.branch_id" :options="[{value:null,text:'Seluruh tenant'},...branches.map(branch=>({value:branch.id,text:branch.name}))]"/></b-form-group>
      <p class="text-muted">
        Seluruh pengguna aktif akan dimasukkan ke review.
      </p></b-modal
    >
    <b-modal id="invite-modal" size="xl" title="Undang Pengguna Baru" @ok.prevent="inviteUser" ok-title="Buat undangan" cancel-title="Batal">
      <div class="invite-sections">
        <section>
          <header><span>1</span><div><h5>Identitas dan pekerjaan utama</h5><p>Akun dibuat sekali dan dapat dipakai di beberapa cabang.</p></div></header>
          <div class="invite-grid">
            <b-form-group label="Nama lengkap"><b-form-input v-model="inviteForm.name" placeholder="Nama sesuai identitas kerja" /></b-form-group>
            <b-form-group label="Email untuk aktivasi"><b-form-input v-model="inviteForm.email" type="email" placeholder="nama@klinik.id" /></b-form-group>
            <b-form-group label="Jenis pengguna"><b-form-select v-model="inviteForm.user_type_id" :options="userTypeOptions" @change="handleInviteUserType" /></b-form-group>
            <b-form-group v-if="inviteForm.user_type_id === 3" label="Jabatan" description="Menentukan akses bawaan pekerjaan utama"><b-form-select v-model="inviteForm.position_id" :options="positionOptions" /></b-form-group>
          </div>
        </section>

        <section>
          <header><span>2</span><div><h5>Role akses fungsional</h5><p>{{ inviteForm.user_type_id === 3 ? 'Opsional. Pilih hanya jika tugasnya melampaui akses bawaan jabatan.' : 'Pilih minimal satu role untuk menentukan akses klinis dokter.' }}</p></div></header>
          <div class="invite-assignment-list">
            <div v-if="inviteForm.role_assignments.length" class="invite-assignment-head"><span>Role</span><span>Berlaku di cabang</span><span>Berakhir</span><span></span></div>
            <div v-for="(assignment,index) in inviteForm.role_assignments" :key="index" class="invite-assignment-row"><b-form-select v-model="assignment.role_id" :options="roleOptions" aria-label="Role awal"/><b-form-select v-model="assignment.branch_id" :options="inviteRoleBranchOptions" aria-label="Cakupan cabang role"/><b-form-input v-model="assignment.valid_until" type="date" aria-label="Akhir masa role"/><button type="button" title="Hapus role" @click="inviteForm.role_assignments.splice(index,1)"><i class="fas fa-times"></i></button></div>
            <b-button size="sm" variant="outline-primary" @click="addInviteRole">+ Tambah role</b-button>
            <p v-if="!inviteForm.role_assignments.length" class="text-muted mt-2">Karyawan dapat hanya memakai baseline jabatan. Dokter wajib memiliki minimal satu role.</p>
          </div>
        </section>

        <section>
          <header><span>3</span><div><h5>Cakupan cabang</h5><p>Cabang kerja menentukan lokasi yang dapat dibuka. Konsolidasi hanya menggabungkan data dari cabang yang sudah dipilih.</p></div></header>
          <div class="invite-branch-grid">
            <div class="branch-access-table"><div v-for="row in inviteForm.branches" :key="row.branch_id" class="branch-access-row" :class="{selected:row.assigned}"><b-form-checkbox v-model="row.assigned" @change="normalizeInviteDefault"><strong>{{branchName(row.branch_id)}}</strong></b-form-checkbox><b-form-input v-model="row.valid_from" type="date" :disabled="!row.assigned"/><b-form-input v-model="row.valid_until" type="date" :disabled="!row.assigned"/></div></div>
            <div>
              <b-form-group label="Cabang utama setelah login"><b-form-select v-model="inviteForm.default_branch_id" :options="inviteBranchOptions" /></b-form-group>
              <div class="consolidated-option" :class="{ disabled: selectedInviteBranchRows.length < 2 }">
                <b-form-checkbox v-model="inviteForm.can_view_consolidated" switch :disabled="selectedInviteBranchRows.length < 2">Dashboard konsolidasi</b-form-checkbox>
                <small>Menampilkan ringkasan gabungan tanpa berpindah cabang. Disarankan hanya untuk owner atau manajer regional.</small>
              </div>
            </div>
          </div>
        </section>

        <section class="invite-optional">
          <header><span>4</span><div><h5>Masa berlaku</h5><p>Opsional untuk kontrak, dokter pengganti, atau penugasan sementara.</p></div></header>
          <div class="invite-validity">
            <b-form-group label="Tautan undangan berlaku"><b-input-group append="hari"><b-form-input v-model.number="inviteForm.expires_in_days" type="number" min="1" max="30" /></b-input-group></b-form-group>
            <b-form-group label="Cara pengiriman"><b-form-select v-model="inviteForm.delivery_channel" :options="[{value:'manual',text:'Salin tautan secara manual'},{value:'email',text:'Kirim melalui email sistem'}]"/></b-form-group>
          </div>
        </section>
      </div>
    </b-modal>
    <b-modal
      id="review-detail-modal"
      size="lg"
      title="Detail Review Akses"
      hide-footer
      ><div v-if="reviewDetail">
        <b-alert show variant="info"
          >Keputusan <strong>Cabut</strong> langsung mencabut role, izin
          langsung, ruang lingkup, dan sesi aktif pengguna. Pilih
          <strong>Ubah</strong> bila akses perlu disesuaikan secara
          manual.</b-alert
        ><b-table
          :items="reviewDetail.items"
          :fields="reviewItemFields"
          responsive
          small
          ><template #cell(access_snapshot)="d"><div class="review-snapshot"><strong>{{ snapshotSummary(d.value).permissions }} izin</strong><small>{{ snapshotSummary(d.value).branches }} cabang · {{ snapshotSummary(d.value).roles }} role</small></div></template
          ><template #cell(decision)="d"
            ><span class="status-pill" :class="'is-' + d.value">{{
              decisionLabel(d.value)
            }}</span></template
          ><template #cell(actions)="d"
            ><b-button
              v-if="d.item.decision === 'pending'"
              size="sm"
              variant="primary"
              @click="decideReview(d.item)"
              >Putuskan</b-button
            ></template
          ></b-table
        >
      </div></b-modal
    >
    <b-modal id="review-decision-modal" title="Keputusan Review Akses" @ok.prevent="submitReviewDecision" ok-title="Simpan keputusan" cancel-title="Batal">
      <b-form-group label="Keputusan"><b-form-radio-group v-model="reviewDecision.decision" :options="[{value:'keep',text:'Pertahankan seluruh akses'},{value:'modify',text:'Ubah akses tertentu'},{value:'revoke',text:'Cabut seluruh akses dan nonaktifkan akun'}]" stacked/></b-form-group>
      <template v-if="reviewDecision.decision === 'modify'">
        <b-alert show variant="info">Pilih role atau cabang yang harus dicabut. Hak akses khusus yang dicentang akan dipertahankan.</b-alert>
        <b-form-group label="Cabut penugasan role"><b-form-checkbox-group v-model="reviewDecision.revoke_role_assignment_ids" :options="reviewRoleOptions" stacked/></b-form-group>
        <b-form-group label="Cabut akses cabang"><b-form-checkbox-group v-model="reviewDecision.revoke_branch_ids" :options="reviewBranchOptions" stacked/></b-form-group>
        <b-form-group label="Pertahankan hak akses khusus"><b-form-checkbox-group v-model="reviewDecision.direct_access_codes" :options="reviewDirectOptions" stacked/></b-form-group>
      </template>
      <b-form-group label="Catatan reviewer"><b-form-textarea v-model="reviewDecision.note" rows="3" placeholder="Dasar keputusan dan tindak lanjut"/></b-form-group>
    </b-modal>
  </main>
</template>

<script>
import api from "@/core/services/api.service.js";
import PageHeader from "@/component/general/PageHeader.vue";
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
export default {
  name: "SecurityCenter",
  components: { PageHeader },
  data: () => ({
    activeTab: 0,
    users: [],
    invitations: [],
    roles: [],
    permissionOptions: [],
    permissionSearch: "",
    positions: [],
    reviews: [],
    approvals: [],
    policy: null,
    totalRows: 0,
    page: 1,
    pageSize: 25,
    loadingUsers: false,
    timer: null,
    filters: { search: "", status: "" },
    selectedUser: null,
    sessions: [],
    effectiveCodes: [],
    accessSources: { position: [], role: [], direct: [] },
    redundantAccess: [],
    userConflicts: [],
    assignedRoles: [],
    branches: [],
    branchForm: { rows: [], default_branch_id: null, can_view_consolidated: false, reason: "" },
    reviewDetail: null,
    reviewDecisionItem: null,
    reviewDecision: { decision:"keep", note:"", revoke_role_assignment_ids:[], revoke_branch_ids:[], direct_access_codes:[] },
    userForm: { status: "active", access_expires_at: "", reason: "" },
    assignForm: { role_id: null, branch_id: null, valid_from: "", valid_until: "", reason: "" },
    inviteForm: {
      name: "",
      email: "",
      user_type_id: 3,
      position_id: null,
      role_assignments: [],
      branches: [],
      default_branch_id: null,
      can_view_consolidated: false,
      expires_in_days: 7,
      delivery_channel: "manual",
    },
    roleForm: {},
    reviewForm: {
      name: "Review Akses Kuartalan",
      reviewer_id: null,
      due_date: "",
      branch_id: null,
    },
    statusOptions: [
      { value: "", text: "Semua status" },
      { value: "active", text: "Aktif" },
      { value: "inactive", text: "Nonaktif" },
      { value: "locked", text: "Terkunci" },
      { value: "invited", text: "Diundang" },
    ],
    userTypeOptions: [
      { value: 3, text: "Karyawan / Administrator" },
      { value: 1, text: "Dokter" },
    ],
    userFields: [
      { key: "name", label: "Pengguna" },
      { key: "position_name", label: "Jabatan" },
      { key: "branches", label: "Cabang" },
      { key: "status", label: "Status" },
      { key: "security", label: "Keamanan" },
      { key: "last_login_at", label: "Login terakhir" },
      { key: "active_sessions", label: "Sesi" },
      { key: "actions", label: "" },
    ],
    reviewFields: [
      { key: "name", label: "Review" },
      { key: "status", label: "Status" },
      { key: "progress", label: "Progres" },
      { key: "due_date", label: "Tenggat" },
      { key: "actions", label: "" },
    ],
    invitationFields: [
      { key: "name", label: "Pengguna" },
      { key: "email", label: "Email" },
      { key: "status", label: "Status aktivasi" },
      { key: "invited_by_name", label: "Diundang oleh" },
      { key: "created_at", label: "Dibuat" },
      { key: "expires_at", label: "Berlaku sampai" },
      { key: "accepted_at", label: "Diaktifkan" },
      { key: "actions", label: "" },
    ],
    approvalFields: [
      { key: "request_type", label: "Perubahan" }, { key: "target_user_name", label: "Pengguna" },
      { key: "branch_name", label: "Cabang" }, { key: "requested_by_name", label: "Diajukan oleh" },
      { key: "reason", label: "Alasan" }, { key: "status", label: "Status" }, { key: "created_at", label: "Diajukan" }, { key: "actions", label: "" },
    ],
    reviewItemFields: [
      { key: "name", label: "Pengguna" },
      { key: "email", label: "Email" },
      { key: "access_snapshot", label: "Akses" },
      { key: "decision", label: "Keputusan" },
      { key: "actions", label: "" },
    ],
    sessionFields: [
      { key: "name", label: "Perangkat/Sesi" },
      { key: "created_at", label: "Dibuat" },
      { key: "expires_at", label: "Berakhir" },
      { key: "actions", label: "" },
    ],
  }),
  computed: {
    attentionCount() {
      return this.users.filter(
        (u) => u.status !== "active" || u.risk_count || !u.mfa_enabled
      ).length;
    },
    mfaCount() {
      return this.users.filter((u) => u.mfa_enabled).length;
    },
    roleOptions() {
      return [
        { value: null, text: "Pilih role" },
        ...this.roles
          .filter((r) => r.is_active)
          .map((r) => ({ value: r.id, text: r.name })),
      ];
    },
    activeRoles() {
      return this.roles.filter(role => role.is_active);
    },
    positionOptions() {
      return [
        { value: null, text: "Pilih jabatan", disabled: true },
        ...this.positions.map(position => ({ value: Number(position.id), text: position.name })),
      ];
    },
    filteredPermissionOptions() {
      const term = this.permissionSearch.trim().toLowerCase();
      if (!term) return this.permissionOptions;
      return this.permissionOptions.filter(option => option.text.toLowerCase().includes(term));
    },
    userOptions() {
      return [
        { value: null, text: "Pilih reviewer" },
        ...this.users.map((u) => ({
          value: u.id,
          text: u.name + " · " + u.email,
        })),
      ];
    },
    branchOptions() {
      return this.branches.map(branch => ({ value: branch.id, text: `${branch.name}${branch.is_head_office ? " · Pusat" : ""}` }));
    },
    selectedBranchOptions() {
      return [{ value: null, text: "Pilih cabang default", disabled: true }, ...this.selectedBranchRows.map(row => ({ value: row.branch_id, text: this.branchName(row.branch_id) }))];
    },
    inviteBranchOptions() {
      return [{ value: null, text: "Pilih cabang default", disabled: true }, ...this.selectedInviteBranchRows.map(row => ({ value: row.branch_id, text: this.branchName(row.branch_id) }))];
    },
    selectedBranchRows() { return (this.branchForm.rows || []).filter(row => row.assigned); },
    selectedInviteBranchRows() { return (this.inviteForm.branches || []).filter(row => row.assigned); },
    roleBranchOptions() { return [{value:null,text:"Seluruh cabang kerja"},...this.selectedBranchRows.map(row=>({value:row.branch_id,text:this.branchName(row.branch_id)}))]; },
    inviteRoleBranchOptions() { return [{value:null,text:"Seluruh cabang kerja"},...this.selectedInviteBranchRows.map(row=>({value:row.branch_id,text:this.branchName(row.branch_id)}))]; },
    reviewSnapshot(){return this.reviewDecisionItem?this.parseSnapshot(this.reviewDecisionItem.access_snapshot):{};},
    reviewRoleOptions(){const snapshot=this.reviewSnapshot;if(Array.isArray(snapshot))return[];const result=[];((snapshot.sources&&snapshot.sources.tenant_roles)||[]).forEach(role=>result.push({value:Number(role.id),text:`${role.name} · Seluruh cabang`}));(snapshot.branches||[]).forEach(branch=>(branch.role_assignments||[]).forEach(role=>result.push({value:Number(role.id),text:`${role.name} · ${branch.name}`})));return result;},
    reviewBranchOptions(){const snapshot=this.reviewSnapshot;return Array.isArray(snapshot)?[]:(snapshot.branches||[]).map(branch=>({value:Number(branch.id),text:branch.name}));},
    reviewDirectOptions(){const snapshot=this.reviewSnapshot;return Array.isArray(snapshot)?[]:(((snapshot.sources||{}).direct)||[]).map(code=>({value:String(code),text:String(code)}));},
  },
  methods: {
    async loadBranches() {
      try {
        const response = await api.get("identity-security/branches", "");
        this.branches = response.data.data || [];
      } catch (e) {
        this.branches = [];
        this.toast("Daftar cabang gagal dimuat. Pastikan Anda memiliki izin Kelola Cabang.", "warning");
      }
    },
    async loadPositions() {
      try {
        const response = await api.get("identity-security/positions", "");
        this.positions = response.data.data || [];
      } catch (e) {
        this.positions = [];
        this.toast("Daftar jabatan gagal dimuat.", "warning");
      }
    },
    async loadUsers(page = 1) {
      this.page = page;
      this.loadingUsers = true;
      const p = new URLSearchParams({ page, page_size: this.pageSize });
      Object.keys(this.filters).forEach((k) => {
        if (this.filters[k]) p.append(k, this.filters[k]);
      });
      try {
        const r = await api.get("identity-security/users", "?" + p);
        this.users = r.data.data || [];
        this.totalRows = r.data.meta.pagination.total;
      } catch (e) {
        this.toast("Data pengguna gagal dimuat.", "danger");
      } finally {
        this.loadingUsers = false;
      }
    },
    scheduleUsers() {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.loadUsers(1), 400);
    },
    async loadRoles() {
      try {
        const r = await api.get("access-governance/roles");
        this.roles = r.data.data || [];
      } catch (e) {}
    },
    async loadInvitations() {
      try {
        const response = await api.get("identity-security/invitations");
        this.invitations = response.data.data || [];
      } catch (e) {
        this.invitations = [];
        this.toast("Daftar undangan gagal dimuat.", "danger");
      }
    },
    async loadPermissionCatalog() {
      try {
        const response = await api.get("access-governance/permissions");
        this.permissionOptions = (response.data.data || [])
          .filter(item => item.assignable)
          .map((item) => ({ value: String(item.code), text: `${item.section_name} · ${item.name} (${item.code})` }));
      } catch (e) {
        this.permissionOptions = [];
      }
    },
    async loadReviews() {
      try {
        const r = await api.get("access-governance/reviews");
        this.reviews = r.data.data || [];
      } catch (e) {}
    },
    async loadApprovals() {
      try { const r = await api.get("access-governance/approvals"); this.approvals = r.data.data || []; }
      catch (e) { this.approvals = []; this.toast("Daftar persetujuan gagal dimuat.", "danger"); }
    },
    async loadPolicy() {
      try {
        const r = await api.get("identity-security/policy");
        this.policy = r.data.data;
      } catch (e) {}
    },
    async openUser(user) {
      this.selectedUser = user;
      this.userForm = {
        status: user.status === "expired" ? "inactive" : user.status,
        access_expires_at: user.access_expires_at
          ? String(user.access_expires_at).slice(0, 16)
          : "",
        reason: "",
      };
      this.assignForm = { role_id: null, branch_id: null, valid_from: "", valid_until: "", reason: "" };
      this.$bvModal.show("user-security-modal");
      await Promise.all([this.refreshUserDetail(), this.loadUserBranchAccess()]);
    },
    async loadUserBranchAccess() {
      const response = await api.get("identity-security/users/" + this.selectedUser.id + "/branch-access");
      const data = response.data.data || {};
      this.branchForm = {
        rows: (data.branches || []).map(branch => ({ branch_id:Number(branch.id), assigned:!!branch.assigned, valid_from:branch.valid_from ? String(branch.valid_from).slice(0,10) : "", valid_until:branch.valid_until ? String(branch.valid_until).slice(0,10) : "" })),
        default_branch_id: data.default_branch_id ? Number(data.default_branch_id) : null,
        can_view_consolidated: !!data.can_view_consolidated,
        reason: "",
      };
    },
    normalizeBranchDefault() {
      const ids=this.selectedBranchRows.map(row=>row.branch_id);if (!ids.includes(this.branchForm.default_branch_id)) this.branchForm.default_branch_id = ids[0] || null;
      if (ids.length < 2) this.branchForm.can_view_consolidated = false;
    },
    normalizeInviteDefault() {
      const ids=this.selectedInviteBranchRows.map(row=>row.branch_id);if (!ids.includes(this.inviteForm.default_branch_id)) this.inviteForm.default_branch_id = ids[0] || null;
      if (ids.length < 2) this.inviteForm.can_view_consolidated = false;
    },
    async saveUserBranchAccess() {
      if (!this.selectedBranchRows.length || !this.branchForm.default_branch_id) return this.toast("Pilih minimal satu cabang dan tentukan cabang default.", "warning");
      if ((this.branchForm.reason || "").length < 5) return this.toast("Isi alasan perubahan minimal 5 karakter.","warning");
      const payload={branches:this.selectedBranchRows.map(({branch_id,valid_from,valid_until})=>({branch_id,valid_from,valid_until})),default_branch_id:this.branchForm.default_branch_id,can_view_consolidated:this.branchForm.can_view_consolidated,reason:this.branchForm.reason};
      const response=await api.update("identity-security/users", this.selectedUser.id + "/branch-access", payload);
      this.toast(response.data.message || "Akses cabang pengguna berhasil disimpan.",response.data.status === "pending_approval" ? "warning" : "success");
      await Promise.all([this.loadUsers(this.page), this.loadUserBranchAccess()]);
    },
    async refreshUserDetail() {
      const r = await api.get(
        "identity-security/users/" + this.selectedUser.id
      );
      this.sessions = r.data.data.sessions || [];
      this.effectiveCodes = r.data.data.effective_access || [];
      this.accessSources = r.data.data.access_sources || { position: [], role: [], direct: [] };
      this.redundantAccess = r.data.data.redundant_access || [];
      this.userConflicts = r.data.data.conflicts || [];
      this.assignedRoles = r.data.data.roles || [];
    },
    async saveUserStatus() {
      if (!this.userForm.reason)
        return this.toast("Alasan perubahan wajib diisi.", "warning");
      await api.update(
        "identity-security/users",
        this.selectedUser.id + "/status",
        this.userForm
      );
      this.toast("Status pengguna diperbarui.");
      this.$bvModal.hide("user-security-modal");
      this.loadUsers(this.page);
    },
    async forceLogout() {
      const response=await api.post(
        "identity-security/users/" + this.selectedUser.id + "/force-logout",
        {}
      );
      this.sessions = [];
      this.toast("Semua sesi berhasil dicabut.");
    },
    async resetPassword() {
      if (!this.userForm.reason)
        return this.toast("Isi alasan tindakan terlebih dahulu.", "warning");
      const r = await api.post(
        "identity-security/users/" + this.selectedUser.id + "/reset-password",
        { reason: this.userForm.reason }
      );
      this.$bvModal.msgBoxOk(
        "Password sementara: " + r.data.temporary_password,
        { title: "Salin dan kirim secara aman" }
      );
      this.sessions = [];
    },
    async disableMfa() {
      if (!this.userForm.reason)
        return this.toast("Isi alasan tindakan terlebih dahulu.", "warning");
      const response = await api.post(
        "identity-security/users/" + this.selectedUser.id + "/mfa/disable",
        { reason: this.userForm.reason }
      );
      this.toast("MFA direset dan semua sesi dicabut.");
      this.selectedUser.mfa_enabled = false;
    },
    async revokeSession(id) {
      await api.delete(
        "identity-security/users/" + this.selectedUser.id + "/sessions/" + id
      );
      this.sessions = this.sessions.filter((s) => s.id !== id);
      this.toast("Sesi dicabut.");
    },
    async assignRole() {
      if (!this.assignForm.role_id || this.assignForm.reason.length < 5)
        return this.toast(
          "Pilih role dan isi alasan minimal 5 karakter.",
          "warning"
        );
      const response = await api.post(
        "access-governance/roles/" +
          this.assignForm.role_id +
          "/users/" +
          this.selectedUser.id,
        this.assignForm
      );
      this.toast(response.data.message || "Role berhasil diberikan.",response.data.status === "pending_approval" ? "warning" : "success");
      this.assignForm = { role_id: null, branch_id: null, valid_from: "", valid_until: "", reason: "" };
      await this.refreshUserDetail();
    },
    async revokeRole(role) {
      const ok = await this.$bvModal.msgBoxConfirm(
        "Cabut role " + role.name + " dari " + this.selectedUser.name + "?",
        {
          title: "Konfirmasi pencabutan",
          okVariant: "danger",
          okTitle: "Cabut",
          cancelTitle: "Batal",
        }
      );
      if (!ok) return;
      const reason=window.prompt("Alasan pencabutan role (minimal 5 karakter)","Penugasan sudah berakhir");if(!reason||reason.length<5)return;
      const response=await api.post("access-governance/roles/" + role.id + "/users/" + this.selectedUser.id + "/revoke",{assignment_id:role.assignment_id,reason});
      this.toast(response.data.message || "Role berhasil dicabut.",response.data.status === "pending_approval" ? "warning" : "success");
      await this.refreshUserDetail();
    },
    newRole() {
      this.roleForm = {
        id: null,
        code: "",
        name: "",
        description: "",
        access_codes: [],
        is_active: true,
        reason: "",
      };
      this.permissionSearch = "";
      this.$bvModal.show("role-modal");
    },
    editRole(r) {
      this.roleForm = {
        id: r.id,
        code: r.code,
        name: r.name,
        description: r.description,
        access_codes: (r.access_codes || []).map(String),
        is_active: !!r.is_active,
        reason: "",
      };
      this.permissionSearch = "";
      this.$bvModal.show("role-modal");
    },
    syncRoleCode() {
      if (this.roleForm.id) return;
      this.roleForm.code = String(this.roleForm.name || "")
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "")
        .slice(0, 80);
    },
    openInvite() {
      const headOffice = this.branches.find(branch => branch.is_head_office) || this.branches[0];
      this.inviteForm = {
        name: "",
        email: "",
        user_type_id: 3,
        position_id: null,
        role_assignments: [],
        branches: this.branches.map(branch=>({branch_id:Number(branch.id),assigned:!!(headOffice&&Number(branch.id)===Number(headOffice.id)),valid_from:"",valid_until:""})),
        default_branch_id: headOffice ? Number(headOffice.id) : null,
        can_view_consolidated: false,
        expires_in_days: 7,
        delivery_channel: "manual",
      };
      this.$bvModal.show("invite-modal");
    },
    handleInviteUserType(type) {
      const numericType = Number(type);
      this.inviteForm.user_type_id = numericType;
      if (numericType === 1) {
        this.inviteForm.position_id = null;
        const doctorRole = this.roles.find(role => role.is_active && role.code === "doctor");
        this.inviteForm.role_assignments = doctorRole ? [{role_id:Number(doctorRole.id),branch_id:null,valid_from:"",valid_until:""}] : [];
      } else {
        this.inviteForm.role_assignments = [];
      }
    },
    async saveRole() {
      if (!this.roleForm.name || !this.roleForm.code || !(this.roleForm.access_codes || []).length)
        return this.toast("Nama role, kode internal, dan minimal satu izin wajib diisi.", "warning");
      const data = {
        code: this.roleForm.code,
        name: this.roleForm.name,
        description: this.roleForm.description,
        is_active: this.roleForm.is_active,
        access_codes: this.roleForm.access_codes || [],
        reason: this.roleForm.reason || "Memperbarui template role fungsional",
      };
      const response = this.roleForm.id
        ? await api.update("access-governance/roles", this.roleForm.id, data)
        : await api.post("access-governance/roles", data);
      this.$bvModal.hide("role-modal");
      this.toast(response.data.message || "Role berhasil disimpan.", response.data.status === "pending_approval" ? "warning" : "success");
      this.loadRoles();
    },
    async inviteUser() {
      if (!this.inviteForm.name || !this.inviteForm.email)
        return this.toast("Nama lengkap dan email aktivasi wajib diisi.", "warning");
      if (this.inviteForm.user_type_id === 3 && !this.inviteForm.position_id)
        return this.toast("Pilih jabatan untuk menentukan akses bawaan karyawan.", "warning");
      if (this.inviteForm.user_type_id === 1 && !this.inviteForm.role_assignments.length)
        return this.toast("Dokter wajib memiliki minimal satu role akses.", "warning");
      if (!this.selectedInviteBranchRows.length || !this.inviteForm.default_branch_id)
        return this.toast("Pilih minimal satu cabang kerja dan cabang utama.", "warning");
      const payload={...this.inviteForm,branches:this.selectedInviteBranchRows.map(({branch_id,valid_from,valid_until})=>({branch_id,valid_from,valid_until})),role_assignments:this.inviteForm.role_assignments.filter(row=>row.role_id),activation_base_url:window.location.origin+window.location.pathname+"#/invitation/"};
      const r = await api.post(
        "identity-security/invitations",
        payload
      );
      const link = r.data.data.invitation_token ?
        window.location.origin +
        window.location.pathname +
        "#/invitation/" +
        r.data.data.invitation_token : null;
      this.$bvModal.hide("invite-modal");
      if(link)await this.$bvModal.msgBoxOk(link,{title:"Tautan undangan — salin dan kirim secara aman",size:"lg"});else this.toast("Undangan dikirim melalui email.");
      this.inviteForm = {
        name: "",
        email: "",
        user_type_id: 3,
        position_id: null,
        role_assignments: [],
        branches: [],
        default_branch_id: null,
        can_view_consolidated: false,
        expires_in_days: 7,
        delivery_channel: "manual",
      };
      this.loadUsers(1);
      this.loadInvitations();
    },
    async createReview() {
      await api.post("access-governance/reviews", this.reviewForm);
      this.$bvModal.hide("review-modal");
      this.toast("Review akses berhasil dimulai.");
      this.loadReviews();
    },
    addInviteRole(){this.inviteForm.role_assignments.push({role_id:null,branch_id:null,valid_from:"",valid_until:""});},
    branchName(id){const branch=this.branches.find(item=>Number(item.id)===Number(id));return branch?branch.name:"Cabang";},
    async resendInvitation(invitation){const response=await api.post(`identity-security/invitations/${invitation.id}/resend`,{expires_in_days:7,delivery_channel:invitation.delivery_channel||"manual",activation_base_url:window.location.origin+window.location.pathname+"#/invitation/"});const token=response.data.data.invitation_token;if(token)await this.$bvModal.msgBoxOk(window.location.origin+window.location.pathname+"#/invitation/"+token,{title:"Tautan undangan baru",size:"lg"});else this.toast("Undangan baru dikirim.");await this.loadInvitations();},
    async revokeInvitation(invitation){const reason=window.prompt("Alasan pembatalan undangan (minimal 5 karakter)","Undangan tidak lagi diperlukan");if(!reason||reason.length<5)return;await api.post(`identity-security/invitations/${invitation.id}/revoke`,{reason});this.toast("Undangan dibatalkan.");await Promise.all([this.loadInvitations(),this.loadUsers(this.page)]);},
    async decideApproval(item,decision){const note=window.prompt(decision==="approve"?"Catatan persetujuan":"Alasan penolakan",decision==="approve"?"Kebutuhan akses telah diverifikasi":"Kebutuhan akses belum sesuai");if(!note||note.length<5)return;const response=await api.update("access-governance/approvals",item.id,{decision,note});this.toast(response.data.message);await Promise.all([this.loadApprovals(),this.loadUsers(this.page)]);if(this.selectedUser)await this.refreshUserDetail();},
    async openReview(review) {
      const r = await api.get("access-governance/reviews/" + review.id);
      this.reviewDetail = r.data.data;
      this.$bvModal.show("review-detail-modal");
    },
    async decideReview(item) {
      this.reviewDecisionItem=item;const snapshot=this.parseSnapshot(item.access_snapshot);const direct=Array.isArray(snapshot)?[]:(((snapshot.sources||{}).direct)||[]).map(String);
      this.reviewDecision={decision:"keep",note:"",revoke_role_assignment_ids:[],revoke_branch_ids:[],direct_access_codes:direct};this.$bvModal.show("review-decision-modal");
    },
    async submitReviewDecision(){
      if(!this.reviewDecision.note||this.reviewDecision.note.length<3)return this.toast("Catatan reviewer minimal 3 karakter.","warning");
      if(this.reviewDecision.decision==="modify"&&!this.reviewDecision.revoke_role_assignment_ids.length&&!this.reviewDecision.revoke_branch_ids.length&&JSON.stringify(this.reviewDecision.direct_access_codes.sort())===JSON.stringify(this.reviewDirectOptions.map(x=>x.value).sort()))return this.toast("Pilih perubahan akses yang akan diterapkan.","warning");
      await api.update(
        "access-governance/reviews/" + this.reviewDetail.review.id + "/items",
        this.reviewDecisionItem.id,
        this.reviewDecision
      );
      this.$bvModal.hide("review-decision-modal");
      this.toast("Keputusan review disimpan.");
      await this.openReview(this.reviewDetail.review);
      this.loadReviews();
    },
    async savePolicy() {
      await api.update("identity-security", "policy", this.policy);
      this.toast("Kebijakan keamanan disimpan.");
    },
    initials(v) {
      return (v || "S")
        .split(" ")
        .slice(0, 2)
        .map((x) => x[0])
        .join("")
        .toUpperCase();
    },
    statusLabel(v) {
      return (
        {
          active: "Aktif",
          inactive: "Nonaktif",
          locked: "Terkunci",
          invited: "Diundang",
          expired: "Kedaluwarsa",
        }[v] || v
      );
    },
    decisionLabel(v) {
      return (
        {
          pending: "Menunggu",
          keep: "Pertahankan",
          revoke: "Cabut",
          modify: "Ubah",
        }[v] || v
      );
    },
    invitationStatusLabel(v) {
      return ({ pending: "Menunggu aktivasi", accepted: "Sudah aktif", expired: "Kedaluwarsa", revoked:"Dibatalkan" })[v] || v;
    },
    approvalStatusLabel(v){return({pending:"Menunggu",approved:"Disetujui",rejected:"Ditolak",failed:"Gagal diterapkan",applied:"Diterapkan"})[v]||v;},
    snapshotSummary(v){const snapshot=this.parseSnapshot(v);if(Array.isArray(snapshot))return{permissions:snapshot.length,branches:0,roles:0};const roles=((snapshot.sources&&snapshot.sources.tenant_roles)||[]).length+(snapshot.branches||[]).reduce((sum,branch)=>sum+(branch.role_assignments||[]).length,0);return{permissions:(snapshot.effective_codes||[]).length,branches:(snapshot.branches||[]).length,roles};},
    parseSnapshot(v) {
      try {
        return Array.isArray(v) ? v : JSON.parse(v || "[]");
      } catch (e) {
        return [];
      }
    },
    formatDate(v, time = true) {
      if (!v) return "-";
      return new Intl.DateTimeFormat(
        "id-ID",
        time
          ? { dateStyle: "medium", timeStyle: "short" }
          : { dateStyle: "medium" }
      ).format(new Date(v));
    },
    toast(message, variant = "success") {
      this.$bvToast.toast(message, {
        title: variant === "success" ? "Berhasil" : "Perhatian",
        variant,
        solid: true,
      });
    },
  },
  async mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: "Administrasi" },
      { title: "Manajemen Pengguna" },
      { title: "Pengguna & Cabang" },
    ]);
    if (this.$route.query.section === "invitations") this.activeTab = 1;
    if (this.$route.query.section === "roles") this.activeTab = 2;
    await Promise.all([this.loadUsers(), this.loadRoles(), this.loadPermissionCatalog(), this.loadInvitations(), this.loadBranches(), this.loadPositions()]);
  },
  watch: {
    "$route.query.section"(section) {
      if (section === "invitations") {
        this.activeTab = 1;
        this.loadInvitations();
      } else if (section === "roles") {
        this.activeTab = 2;
        this.loadRoles();
      } else if (!section) {
        this.activeTab = 0;
      }
    },
  },
  beforeDestroy() {
    clearTimeout(this.timer);
  },
};
</script>
<style scoped>
.security-center {
  padding-bottom: 2rem;
}
.security-kpis {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
  margin-bottom: 1.2rem;
}
.security-kpis article,
.security-card {
  background: #fff;
  border: 1px solid #e7edf4;
  border-radius: 14px;
  box-shadow: 0 8px 24px rgba(31, 45, 61, 0.05);
}
.security-kpis article {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.1rem;
}
.security-kpis i {
  font-size: 1.3rem;
  color: var(--skin-primary);
}
.security-kpis span,
.identity-cell small {
  display: block;
  color: #718096;
  font-size: 0.8rem;
}
.security-kpis strong {
  font-size: 1.55rem;
}
.security-card {
  padding: 1.25rem;
}
.security-toolbar {
  display: grid;
  grid-template-columns: 2fr 1fr auto;
  gap: 0.75rem;
  margin-bottom: 1rem;
}
.toolbar-actions {
  display: flex;
  gap: 0.5rem;
}
.identity-cell {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-width: 210px;
}
.identity-cell > span {
  display: grid;
  place-items: center;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: #e9f2ff;
  color: #185abc;
  font-weight: 800;
}
.status-pill {
  display: inline-block;
  border-radius: 999px;
  padding: 0.28rem 0.62rem;
  background: #edf1f5;
  font-size: 0.76rem;
  font-weight: 700;
}
.status-pill.is-active,
.status-pill.is-completed,
.status-pill.is-keep {
  background: #e5f7ee;
  color: #137047;
}
.status-pill.is-locked,
.status-pill.is-inactive,
.status-pill.is-expired,
.status-pill.is-revoke {
  background: #fff0ef;
  color: #b42318;
}
.status-pill.is-in_progress,
.status-pill.is-invited,
.status-pill.is-pending,
.status-pill.is-modify {
  background: #fff6df;
  color: #8a6200;
}
.security-flags {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  font-size: 0.78rem;
}
.security-flags .ok {
  color: #137047;
}
.security-flags .warn {
  color: #9a6700;
}
.security-flags .danger {
  color: #b42318;
}
.security-footer,
.section-heading,
.role-card footer,
.user-detail > header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.role-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
}
.access-model-guide {
  display: grid;
  grid-template-columns: 1fr auto 1fr auto 1fr;
  align-items: center;
  gap: 0.8rem;
  margin-bottom: 1.25rem;
  padding: 1rem;
  border: 1px solid #dce8f8;
  border-radius: 12px;
  background: #f7faff;
}
.access-model-guide > div {
  display: grid;
  grid-template-columns: auto 1fr;
  column-gap: 0.6rem;
}
.access-model-guide > div > span,
.invite-sections section > header > span {
  display: grid;
  place-items: center;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: var(--skin-primary);
  color: #fff;
  font-weight: 800;
}
.access-model-guide strong { align-self: center; }
.access-model-guide small { grid-column: 2; color: #64748b; }
.access-model-guide > i { color: #94a3b8; }
.role-card {
  border: 1px solid #e7edf4;
  border-radius: 12px;
  padding: 1rem;
}
.role-card h4 {
  margin-top: 0.8rem;
}
.role-card p {
  min-height: 42px;
  color: #64748b;
}
.role-icon {
  display: inline-grid;
  place-items: center;
  width: 38px;
  height: 38px;
  background: #edf4ff;
  color: var(--skin-primary);
  border-radius: 9px;
}
.template-label {
  margin-left: 0.5rem;
  font-size: 0.7rem;
  background: #e8f7f0;
  color: #17734b;
  padding: 0.2rem 0.45rem;
  border-radius: 99px;
}
.detail-grid,
.policy-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}
.detail-grid section,
.role-assignment,
.branch-assignment {
  border: 1px solid #e7edf4;
  border-radius: 10px;
  padding: 1rem;
}
.branch-scope-grid,
.invite-validity {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}
.branch-check-list {
  border: 1px solid #e7edf4;
  border-radius: 9px;
  padding: 0.8rem 1rem;
  max-height: 230px;
  overflow: auto;
}
.branch-check-list .custom-checkbox {
  margin-bottom: 0.55rem;
}
.consolidated-option {
  border: 1px solid #cfe0f5;
  border-radius: 9px;
  padding: 0.75rem;
  background: #f7faff;
}
.consolidated-option.disabled { opacity: 0.62; }
.consolidated-option small { display: block; color: #64748b; margin-top: 0.25rem; }
.role-form-grid,
.invite-grid,
.invite-branch-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}
.permission-picker {
  max-height: 310px;
  overflow: auto;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 0.85rem 1rem;
  background: #fbfdff;
}
.permission-picker .custom-checkbox { padding: 0.35rem 0 0.35rem 1.5rem; border-bottom: 1px solid #f0f3f7; }
.role-form-footer { display: flex; justify-content: space-between; align-items: center; margin-top: 0.8rem; }
.invite-sections { display: grid; gap: 1rem; }
.invite-sections > section { border: 1px solid #e2e8f0; border-radius: 12px; padding: 1rem; }
.invite-sections section > header { display: flex; gap: 0.75rem; margin-bottom: 1rem; }
.invite-sections section > header h5 { margin: 0; }
.invite-sections section > header p { margin: 0.2rem 0 0; color: #64748b; }
.invite-role-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 0.65rem; }
.invite-role-grid > label { display: block; border: 1px solid #dbe3ee; border-radius: 10px; padding: 0.75rem; cursor: pointer; transition: .15s ease; }
.invite-role-grid > label.selected { border-color: var(--skin-primary); background: #f1f6ff; box-shadow: 0 0 0 2px rgba(36,107,253,.08); }
.invite-role-grid small,.invite-role-grid em { display: block; margin-left: 1.5rem; }
.invite-role-grid small { color: #64748b; min-height: 34px; }
.invite-role-grid em { color: var(--skin-primary); font-size: .75rem; margin-top: .35rem; }
.invite-validity { grid-template-columns: 1fr 1fr 1fr; }
.invite-optional { background: #fbfcfe; }
.branch-chip-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  max-width: 260px;
}
.branch-chip-list span {
  background: #e8f7f0;
  color: #17734b;
  border-radius: 99px;
  padding: 0.2rem 0.45rem;
  font-size: 0.72rem;
}
.branch-chip-list em {
  color: #9a6700;
  font-size: 0.78rem;
}
.section-heading.compact p {
  margin: 0.25rem 0 0;
  color: #64748b;
}
.role-assign-form {
  display: grid;
  grid-template-columns: 1.2fr 1.2fr 1fr 1fr 1.5fr auto;
  gap: 0.5rem;
}
.role-assign-form .form-group { margin-bottom:0; }
.role-assign-submit { align-self:end; margin-bottom:1rem; }
.branch-access-table { display: grid; gap: .55rem; }
.branch-access-row { display:grid; grid-template-columns:minmax(190px,1.4fr) 1fr 1fr; gap:.65rem; align-items:center; padding:.65rem; border:1px solid #e2e8f0; border-radius:9px; background:#fafcff; opacity:.68; }
.branch-access-row.selected { border-color:#86aefc; background:#f3f7ff; opacity:1; }
.branch-access-settings { margin-top:.35rem; padding:.8rem; border:1px solid #e2e8f0; border-radius:9px; }
.invite-assignment-list { display:grid; gap:.55rem; }
.invite-assignment-row { display:grid; grid-template-columns:1.4fr 1.2fr 1fr 36px; gap:.55rem; }
.invite-assignment-head { display:grid; grid-template-columns:1.4fr 1.2fr 1fr 36px; gap:.55rem; color:#64748b; font-size:.75rem; font-weight:700; text-transform:uppercase; letter-spacing:.04em; padding:0 .15rem; }
.invite-assignment-row button { border:0; border-radius:7px; color:#b42318; background:#fff0ef; }
.table-actions { display:flex; gap:.35rem; white-space:nowrap; }
.review-snapshot { display:flex; flex-direction:column; }.review-snapshot small{color:#64748b;}
.assigned-role-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.75rem;
}
.assigned-role-list > span {
  background: #edf4ff;
  color: #185abc;
  border-radius: 7px;
  padding: 0.35rem 0.5rem;
}
.assigned-role-list button {
  border: 0;
  background: transparent;
  color: #b42318;
  font-size: 1.1rem;
}
.assigned-role-list small {
  color: #64748b;
}
.access-chip-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}
.access-chip-list span {
  background: #edf4ff;
  color: #185abc;
  padding: 0.25rem 0.5rem;
  border-radius: 6px;
  font-size: 0.76rem;
}
.effective-access-heading { display: flex; justify-content: space-between; align-items: center; }
.effective-access-heading h5 { margin-bottom: .2rem; }
.effective-access-heading p { margin: 0; color: #64748b; }
.access-source-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: .75rem; }
.access-source-grid > section { border: 1px solid #e2e8f0; border-radius: 9px; padding: .8rem; }
.access-source-grid h6 i { color: var(--skin-primary); margin-right: .35rem; }
.access-source-grid em { color: #94a3b8; font-size: .78rem; }
.policy-grid {
  grid-template-columns: repeat(3, 1fr);
}
.policy-switches {
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid #edf1f5;
}
@media (max-width: 992px) {
  .security-kpis,
  .role-grid {
    grid-template-columns: 1fr 1fr;
  }
  .policy-grid {
    grid-template-columns: 1fr 1fr;
  }
  .role-assign-form {
    grid-template-columns: 1fr 1fr;
  }
  .invite-assignment-row { grid-template-columns:1fr 1fr; }
  .invite-role-grid { grid-template-columns: 1fr 1fr; }
  .access-source-grid { grid-template-columns: 1fr; }
  .access-model-guide { grid-template-columns: 1fr; }
  .access-model-guide > i { transform: rotate(90deg); justify-self: center; }
}
@media (max-width: 576px) {
  .security-kpis,
  .role-grid,
  .security-toolbar,
  .detail-grid,
  .policy-grid,
  .role-assign-form {
    grid-template-columns: 1fr;
  }
  .branch-scope-grid,
  .invite-validity,
  .role-form-grid,
  .invite-grid,
  .invite-branch-grid,
  .invite-role-grid {
    grid-template-columns: 1fr;
  }
  .branch-access-row,.invite-assignment-row { grid-template-columns:1fr; }
  .security-footer,
  .section-heading {
    align-items: flex-start;
    flex-direction: column;
    gap: 1rem;
  }
  .toolbar-actions {
    width: 100%;
    flex-direction: column;
  }
}
</style>
